import React from "react";
import { UserFocus, Buildings, Trademark } from "@phosphor-icons/react";

/**
 * Publisher & Author Attribution
 *
 * Reusable block establishing the standard IP relationship:
 *   Derrick Hall  — Author, Creator, Intellectual Property Owner, Trademark Owner
 *   Begin Again Living Ministries  — Publisher, Educational Ministry, Resource Provider
 *
 * Layout-safe: this component sits at the bottom of publication-focused pages
 * (Books & Publications, From Diagnosis to Goodbye) without disturbing any
 * existing sections.
 */
const PublisherAttribution = ({ testIdPrefix = "attribution" }) => (
  <section
    data-testid={`${testIdPrefix}-block`}
    className="py-16 sm:py-20 border-t border-ink-100 bg-white"
    aria-labelledby={`${testIdPrefix}-heading`}
  >
    <div className="container-x max-w-5xl">
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
          <span className="w-8 h-px bg-secondary" />
          Publishing & Attribution
        </span>
        <h2
          id={`${testIdPrefix}-heading`}
          data-testid={`${testIdPrefix}-heading`}
          className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]"
        >
          Author, Publisher &amp; Trademark
        </h2>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6 lg:gap-8">
        {/* ABOUT THE AUTHOR */}
        <article
          data-testid={`${testIdPrefix}-author`}
          className="bg-white border border-ink-100 rounded-3xl p-8 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.08)]"
        >
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary/8 text-primary border border-primary/10">
            <UserFocus size={22} weight="duotone" />
          </span>
          <p className="mt-5 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            About the Author
          </p>
          <h3 className="mt-3 font-heading text-2xl text-ink-900 font-medium leading-snug">
            Derrick Hall
          </h3>
          <p className="mt-2 text-sm text-ink-600">
            Hospice Chaplain &nbsp;&bull;&nbsp; Author &nbsp;&bull;&nbsp; Speaker &nbsp;&bull;&nbsp; Grief Coach
          </p>
          <p className="mt-4 text-ink-700 leading-relaxed text-sm">
            Author and creator of the <em>From Diagnosis to Goodbye</em>{" "}
            Educational Platform.
          </p>
          <p className="mt-3 text-ink-500 leading-relaxed text-xs">
            Derrick Hall is the author, creator, and intellectual property
            owner of the <em>From Diagnosis to Goodbye</em> books,
            workbooks, facilitator resources, participant materials,
            workshops, licensing framework, and educational curriculum.
          </p>
        </article>

        {/* ABOUT THE PUBLISHER */}
        <article
          data-testid={`${testIdPrefix}-publisher`}
          className="bg-white border border-ink-100 rounded-3xl p-8 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.08)]"
        >
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-secondary/12 text-secondary-dark border border-secondary/20">
            <Buildings size={22} weight="duotone" />
          </span>
          <p className="mt-5 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            About the Publisher
          </p>
          <h3 className="mt-3 font-heading text-2xl text-ink-900 font-medium leading-snug">
            Begin Again Living Ministries
          </h3>
          <p className="mt-2 text-sm text-ink-600 italic">
            Publisher &nbsp;&bull;&nbsp; Educational Ministry &nbsp;&bull;&nbsp; Resource Provider
          </p>
          <p className="mt-4 text-ink-700 leading-relaxed text-sm">
            Begin Again Living Ministries serves as the publishing and
            educational ministry through which Derrick Hall&rsquo;s books,
            educational resources, facilitator guides, participant guides,
            workshops, and licensing materials are made available to
            individuals, families, churches, healthcare organizations, and
            communities. Professional courses and structured
            professional-development training are delivered through the
            CCCHAPS Chaplain Training Academy.
          </p>
          <p className="mt-3 text-ink-700 leading-relaxed text-sm">
            The ministry exists to educate, encourage, equip, and support
            those walking alongside others through serious illness,
            caregiving, hospice care, grief, and compassionate ministry.
          </p>
        </article>
      </div>

      {/* TRADEMARK NOTICE */}
      <div
        data-testid={`${testIdPrefix}-trademark`}
        className="mt-8 rounded-2xl bg-softblue/50 border border-primary/10 p-6 flex items-start gap-4"
      >
        <span className="hidden sm:inline-flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-white border border-primary/15 text-primary">
          <Trademark size={20} weight="duotone" />
        </span>
        <div className="text-sm text-ink-700 leading-relaxed">
          <p className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-2">
            Trademark &amp; Ownership
          </p>
          <p>
            <em>From Diagnosis to Goodbye</em> is the proprietary name of
            an educational and professional training platform developed and
            owned by Derrick Hall. A federal trademark application for
            the name is pending with the United States Patent and Trademark
            Office. The name identifies a coordinated series of books,
            workbooks, facilitator guides, participant materials,
            presentations, workshops, seminars, and professional training
            programs. Published by Begin Again Living Ministries with
            permission of the author.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export default PublisherAttribution;
